import React from 'react';
import { Box, ButtonBase, IconButton, Rating, Typography } from '@mui/material';
import AvatarStatusFavorite from '@modules/talents/components/AvatarStatusFavorite';
import StarIcon from '@components/Icon/Star';
import StarActiveIcon from '@components/Icon/StarActiveIcon';
import { useTalentOperations } from '@modules/talents/context/TalentOperationsContext';
import { useEntityNotesDialog } from '@modules/notes/context/EntityNotesDialogContext';
import { Link } from 'react-router-dom';
import DownloadTalentCvButton from '@modules/cv-generator';

/**
 * Normalised talent shape consumed by TalentCell.
 * Both TalentListTable rows and TalentFavoritesList rows should be
 * mapped into this shape before passing to TalentCell.
 */
export interface TalentCellData {
    _id: string;
    fullName?: string;
    firstName?: string;
    lastName?: string;
    photo?: string;
    active?: boolean;
    cvUserData?: any;
    currentStatusUser?: any;
    upercutAverageRating?: number;
    upercutNotes?: any[];
    upercutFiles?: any[];
    upercutFavorites?: Array<{
        _id: string;
        type: string;
        upercutId?: string;
        createdAt: string;
    }>;
    onOpenMedia?: (defaultTab: 'notes' | 'files') => void;
    /** Override the detail link path. Defaults to `/talents/:id/detail` */
    detailPath?: string;
}

/* ------------------------------------------------------------------ */
/*  Name Cell                                                          */
/* ------------------------------------------------------------------ */
interface TalentNameCellProps {
    talent: TalentCellData;
}

/**
 * Renders the "Nom" column content:
 * avatar with status selector + name + notes link (or deactivated badge).
 *
 * All actions (status change, open notes) are handled locally via
 * `useTalentOperations` — no props drilling needed.
 */
export const TalentNameCell: React.FC<TalentNameCellProps> = ({ talent }) => {
    const { updateStatus } = useTalentOperations();
    const { openDialog } = useEntityNotesDialog();
    const talentName = talent.fullName || `${[talent.firstName, talent.lastName].filter(Boolean).join(' ')}`;

    const openNotes = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (talent.onOpenMedia) {
            talent.onOpenMedia('notes');
            return;
        }

        openDialog({
            entityType: 'talent',
            entityId: talent._id,
            entityName: talentName,
        });
    };

    const openFiles = (e: React.MouseEvent) => {
        e.stopPropagation();
        talent.onOpenMedia?.('files');
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
            <AvatarStatusFavorite
                talentIds={talent._id}
                image={talent.photo}
                currentStatus={talent.currentStatusUser}
                upercutFavorites={talent.upercutFavorites}
                onStatusChange={(newStatus) => {
                    const backendStatus = newStatus === 'nothing' ? '' : newStatus;
                    updateStatus([talent._id], backendStatus);
                }}
                shouldStopPropagation
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
                <Box
                    component={Link}
                    to={talent.detailPath ?? `/talents/${talent._id}/detail`}
                    sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        color: theme => theme.palette.secondary.main,
                        textDecoration: 'none',
                        "&:hover": {
                            textDecoration: "underline",
                        },
                    }}>
                    <Typography fontSize="16px" fontWeight={700}>
                        {talentName}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <ButtonBase
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            color: '#CBCBCB',
                            fontSize: '14px',
                            fontWeight: 700,
                            cursor: 'pointer',
                        }}
                        onClick={openNotes}
                    >
                        <i className="fi-br-notebook" />{talent?.upercutNotes?.length || 0} 
                    </ButtonBase>
                    <ButtonBase
                        onClick={openFiles}>
                        <i className="fi-br-clip" style={{ fontSize: '14px', marginRight: '4px' }} />
                        {talent?.upercutFiles?.length || 0}
                    </ButtonBase>
                </Box>
            </Box>
        </Box>
    );
};

/* ------------------------------------------------------------------ */
/*  Rating Cell                                                        */
/* ------------------------------------------------------------------ */
interface TalentRatingCellProps {
    talent: TalentCellData;
}

/**
 * Renders the "Évaluation" column content:
 * a star-based Rating component.
 *
 * On change the rating is persisted via `useTalentOperations`.
 */
export const TalentRatingCell: React.FC<TalentRatingCellProps> = ({ talent }) => {
    const { updateRating } = useTalentOperations();

    return (
        <Box sx={{
            padding: '6px 8px',
            background: theme => theme.palette.background.default,
            borderRadius: '8px',
            display: 'inline-block',
        }}>
            <Rating
                value={talent.upercutAverageRating || 0}
                sx={{ color: '#ffd700', display: 'flex', alignItems: 'center', gap: '8px' }}
                icon={<StarActiveIcon />}
                emptyIcon={<StarIcon />}
                onChange={(_event, newValue) => {
                    updateRating([talent._id], newValue || 0);
                }}
            />
        </Box>
    );
};

/* ------------------------------------------------------------------ */
/*  Action Cells                                                       */
/* ------------------------------------------------------------------ */
interface TalentActionsCellProps {
    talent: TalentCellData;
    onView?: (talent: TalentCellData) => void;
    extraActions?: React.ReactNode;
}

/**
 * Renders the actions column: view, suggest job, and any extra actions.
 */
export const TalentActionsCell: React.FC<TalentActionsCellProps> = ({
    talent,
    onView,
    extraActions,
}) => {
    const { showSuggestJobOfferDialog } = useTalentOperations();
    const cvUserData = talent.cvUserData || talent;
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <IconButton
                color="primary"
                onClick={(e) => {
                    e.stopPropagation();
                    onView?.(talent);
                }}>
                <i className="fi fi-br-eye" />
            </IconButton>
            <IconButton
                color="primary"
                onClick={(e) => {
                    e.stopPropagation();
                    showSuggestJobOfferDialog([talent._id]);
                }}>
                <i className="fi fi-br-briefcase" />
            </IconButton>
            <Box onClick={(e) => e.stopPropagation()}>
                <DownloadTalentCvButton
                    professionList={{ professions: [] }}
                    userData={cvUserData}
                    renderButton={(openModal) => (
                        <IconButton
                            color="primary"
                            onClick={(e) => {
                                e.stopPropagation();
                                openModal();
                            }}>
                            <i className="fi fi-br-file-pdf" />
                        </IconButton>
                    )}
                />
            </Box>
            {extraActions}
        </Box>
    );
};
